import { useGetLeadSources } from '../../api/dealership/customers/audienceFilters/useGetLeadSources'
import { useGetSaleTypes } from '../../api/dealership/customers/audienceFilters/useGetSaleTypes'
import { useGetCustomerSourcesByDealershipID } from '../../api/dealership/customers/sources/useGetCustomerSourcesByDealershipID'
import { Rule, VisibilityCondition } from './types'

const makes = [
  { label: 'Acura', value: 'Acura' },
  { label: 'Alfa Romeo', value: 'Alfa Romeo' },
  { label: 'Aston Martin', value: 'Aston Martin' },
  { label: 'Audi', value: 'Audi' },
  { label: 'Bentley', value: 'Bentley' },
  { label: 'BMW', value: 'BMW' },
  { label: 'Bugatti', value: 'Bugatti' },
  { label: 'Buick', value: 'Buick' },
  { label: 'Cadillac', value: 'Cadillac' },
  { label: 'Chevrolet', value: 'Chevrolet' },
  { label: 'Chrysler', value: 'Chrysler' },
  { label: 'Citroen', value: 'Citroen' },
  { label: 'Dodge', value: 'Dodge' },
  { label: 'Ferrari', value: 'Ferrari' },
  { label: 'Fiat', value: 'Fiat' },
  { label: 'Ford', value: 'Ford' },
  { label: 'Genesis', value: 'Genesis' },
  { label: 'GMC', value: 'GMC' },
  { label: 'Honda', value: 'Honda' },
  { label: 'Hyundai', value: 'Hyundai' },
  { label: 'Infiniti', value: 'Infiniti' },
  { label: 'Jaguar', value: 'Jaguar' },
  { label: 'Jeep', value: 'Jeep' },
  { label: 'Kia', value: 'Kia' },
  { label: 'Lamborghini', value: 'Lamborghini' },
  { label: 'Land Rover', value: 'Land Rover' },
  { label: 'Lexus', value: 'Lexus' },
  { label: 'Lincoln', value: 'Lincoln' },
  { label: 'Lotus', value: 'Lotus' },
  { label: 'Maserati', value: 'Maserati' },
  { label: 'Mazda', value: 'Mazda' },
  { label: 'McLaren', value: 'McLaren' },
  { label: 'Mercedes-Benz', value: 'Mercedes-Benz' },
  { label: 'Mini', value: 'Mini' },
  { label: 'Mitsubishi', value: 'Mitsubishi' },
  { label: 'Nissan', value: 'Nissan' },
  { label: 'Porsche', value: 'Porsche' },
  { label: 'Ram', value: 'Ram' },
  { label: 'Rolls-Royce', value: 'Rolls-Royce' },
  { label: 'Subaru', value: 'Subaru' },
  { label: 'Suzuki', value: 'Suzuki' },
  { label: 'Tesla', value: 'Tesla' },
  { label: 'Toyota', value: 'Toyota' },
  { label: 'Volkswagen', value: 'Volkswagen' },
  { label: 'Volvo', value: 'Volvo' },
]

interface BuilderWithOptions {
  label: string
  value: string
  options: Array<Rule>
}

const evaluateSingleCondition = (
  condition: VisibilityCondition,
  context: Record<string, any>,
): boolean => {
  const fieldValue = context[condition.field]

  switch (condition.operator) {
    case 'includes':
      return Array.isArray(condition.value)
        ? condition.value.includes(fieldValue)
        : fieldValue?.includes(condition.value)

    case 'equals':
      return fieldValue === condition.value

    case 'not_equals':
      return fieldValue !== condition.value

    case 'contains':
      return String(fieldValue)
        .toLowerCase()
        .includes(String(condition.value).toLowerCase())

    case 'starts_with':
      return String(fieldValue)
        .toLowerCase()
        .startsWith(String(condition.value).toLowerCase())

    case 'ends_with':
      return String(fieldValue)
        .toLowerCase()
        .endsWith(String(condition.value).toLowerCase())

    default:
      return false
  }
}

export const evaluateVisibility = (
  condition: VisibilityCondition | VisibilityCondition[],
  context: Record<string, any>,
): boolean => {
  // If array of conditions, all must be true
  if (Array.isArray(condition)) {
    return condition.every((cond) => evaluateSingleCondition(cond, context))
  }
  return evaluateSingleCondition(condition, context)
}

export const audienceBuilderConfig: BuilderWithOptions[] = [
  {
    label: 'General Attributes',
    value: 'general_attributes',
    options: [
      {
        label: 'Customer Source',
        value: 'customer_source_id',
        operators: [
          { label: 'in', value: 'in' },
          { label: 'not in', value: 'not_in' },
        ],
        valueType: 'async-multi-select',
        isVisible: {
          field: 'audienceType',
          operator: 'includes',
          value: ['sales', 'leads', 'service', 'custom'],
        },
        labelKeyName: 'customer_source',
        valueKeyName: 'id',
        searchKeyName: 'customer_source',
        useOptionsHook: useGetCustomerSourcesByDealershipID,
      },
      {
        label: 'Known Customer',
        value: 'known_customer',
        isVisible: {
          field: 'audienceType',
          operator: 'includes',
          value: ['sales', 'leads', 'service', 'custom'],
        },
        operators: [{ label: 'is', value: 'is' }],
        radioOptions: [
          { label: 'True', value: 'true' },
          { label: 'False', value: 'false' },
        ],
        valueType: 'radio',
      },
      {
        label: 'Customer has location',
        value: 'customer_has_location',
        isVisible: {
          field: 'audienceType',
          operator: 'includes',
          value: ['sales', 'leads', 'service', 'custom'],
        },
        operators: [{ label: 'is', value: 'is' }],
        radioOptions: [
          { label: 'True', value: 'true' },
          { label: 'False', value: 'false' },
        ],
        valueType: 'radio',
      },

      {
        label: 'Last Activity',
        value: 'customer_last_activity',
        isVisible: {
          field: 'audienceType',
          operator: 'includes',
          value: ['sales', 'leads', 'service', 'custom'],
        },
        operators: [
          { label: 'on', value: 'is' },
          { label: 'after', value: 'after' },
          { label: 'before', value: 'before' },
        ],
        valueType: 'date',
      },
    ],
  },
  {
    label: 'Lead Attributes',
    value: 'lead_attributes',
    options: [
      {
        label: 'Lead Date',
        value: 'lead_date',
        isVisible: {
          field: 'audienceType',
          operator: 'includes',
          value: ['leads', 'custom'],
        },
        operators: [
          { label: 'on', value: 'is' },
          { label: 'before', value: 'before' },
          { label: 'after', value: 'after' },
          { label: 'not before', value: 'not_before' },
          { label: 'not after', value: 'not_after' },
        ],
        valueType: 'date',
      },
      {
        label: 'Lead Source',
        value: 'lead_source',
        isVisible: {
          field: 'audienceType',
          operator: 'includes',
          value: ['leads', 'custom'],
        },
        operators: [
          { label: 'is', value: 'is' },
          { label: 'is not', value: 'is_not' },
        ],
        valueType: 'async-select',
        labelKeyName: 'lead_source',
        valueKeyName: 'lead_source',
        searchKeyName: 'lead_source',
        useOptionsHook: useGetLeadSources,
      },
      {
        label: 'Make',
        isVisible: {
          field: 'audienceType',
          operator: 'includes',
          value: ['leads', 'custom'],
        },
        value: 'lead_vehicle_make',
        operators: [
          { label: 'is', value: 'is' },
          { label: 'is not', value: 'is_not' },
        ],
        valueType: 'select',
        options: makes,
      },
      {
        label: 'Model',
        value: 'lead_vehicle_model',
        isVisible: {
          field: 'audienceType',
          operator: 'includes',
          value: ['leads', 'custom'],
        },
        operators: [
          { label: 'is', value: 'is' },
          { label: 'is not', value: 'is_not' },
        ],
        valueType: 'text',
      },
      {
        label: 'Year',
        value: 'lead_vehicle_year',
        isVisible: {
          field: 'audienceType',
          operator: 'includes',
          value: ['leads', 'custom'],
        },
        operators: [
          { label: 'is', value: 'is' },
          { label: 'is not', value: 'is_not' },
        ],
        valueType: 'number',
      },
      {
        label: 'Trim',
        value: 'lead_vehicle_trim',
        isVisible: {
          field: 'audienceType',
          operator: 'includes',
          value: ['leads', 'custom'],
        },
        operators: [
          { label: 'is', value: 'is' },
          { label: 'is not', value: 'is_not' },
        ],
        valueType: 'text',
      },
    ],
  },
  {
    label: 'Sale Attributes',
    value: 'sale_attributes',
    options: [
      {
        label: 'Date of car purchase',
        value: 'date_of_car_purchase',
        isVisible: {
          field: 'audienceType',
          operator: 'includes',
          value: ['sales', 'custom'],
        },
        operators: [
          { label: 'on', value: 'is' },
          { label: 'before', value: 'before' },
          { label: 'after', value: 'after' },
          { label: 'not before', value: 'not_before' },
          { label: 'not after', value: 'not_after' },
        ],
        valueType: 'date',
      },
      {
        label: 'Make',
        value: 'sale_vehicle_make',
        isVisible: {
          field: 'audienceType',
          operator: 'includes',
          value: ['sales', 'custom'],
        },
        operators: [
          { label: 'is', value: 'is' },
          { label: 'is not', value: 'is_not' },
        ],
        valueType: 'select',
        options: makes,
      },
      {
        label: 'Model',
        value: 'sale_vehicle_model',
        isVisible: {
          field: 'audienceType',
          operator: 'includes',
          value: ['sales', 'custom'],
        },
        operators: [
          { label: 'is', value: 'is' },
          { label: 'is not', value: 'is_not' },
        ],
        valueType: 'text',
      },
      {
        label: 'Year',
        value: 'sale_vehicle_year',
        isVisible: {
          field: 'audienceType',
          operator: 'includes',
          value: ['sales', 'custom'],
        },
        operators: [
          { label: 'is', value: 'is' },
          { label: 'is not', value: 'is_not' },
        ],
        valueType: 'number',
      },
      {
        label: 'Sale Type',
        value: 'sale_type',
        isVisible: {
          field: 'audienceType',
          operator: 'includes',
          value: ['sales', 'custom'],
        },
        operators: [
          { label: 'is', value: 'is' },
          { label: 'is not', value: 'is_not' },
        ],
        valueType: 'async-select',
        labelKeyName: 'sale_type',
        valueKeyName: 'sale_type',
        searchKeyName: 'sale_type',
        useOptionsHook: useGetSaleTypes,
      },
    ],
  },
  {
    label: 'Custom Options',
    value: 'custom_options',
    options: [
      {
        label: 'Dealership Location',
        value: 'mossy_location',
        isVisible: [
          {
            field: 'dealership_name',
            operator: 'contains',
            value: 'mossy',
          },
          {
            field: 'audienceType',
            operator: 'includes',
            value: ['sales', 'service', 'leads', 'custom'],
          },
        ],
        operators: [
          { label: 'is', value: 'is' },
          { label: 'is not', value: 'is_not' },
        ],
        valueType: 'text',
      },
    ],
  },
  {
    label: 'Service Attributes',
    value: 'service_attributes',
    options: [
      {
        label: 'Last Service Date',
        value: 'last_service_date',
        isVisible: {
          field: 'audienceType',
          operator: 'includes',
          value: ['service', 'custom'],
        },
        operators: [
          { label: 'on', value: 'is' },
          { label: 'before', value: 'before' },
          { label: 'after', value: 'after' },
          { label: 'not before', value: 'not_before' },
          { label: 'not after', value: 'not_after' },

          { label: 'before', value: 'before' },
        ],
        valueType: 'date',
      },
    ],
  },
]
